import './styles.scss'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from 'primereact/button'
import { getLocalUserDetails } from 'Utils/LocalStorage_Handler'
import { fetchCommunityUnreadCount, fetchPersonalChatUnreadCount } from 'Services/Supabase'
import { subscribeToPersonalChatRealtime, subscribeToCommunityChatRealtime } from 'Services/chatRealtimeSubscriptions'

const DraggableChatButton = (props) => {
  const { t } = useTranslation()
  const { setIsChatPanelOpen, isChatPanelOpen, onUnreadCountsChange, unreadCountRefreshRef } = props
  const userDetails = getLocalUserDetails()

  const PADDING = 16
  const BUTTON_MIN_WIDTH = 200
  const INITIAL_OFFSET_X = 40

  const getMaxX = (buttonWidth) => Math.max(0, window.innerWidth - (buttonWidth || BUTTON_MIN_WIDTH) - PADDING)
  const getMaxY = (buttonHeight) => Math.max(0, window.innerHeight - (buttonHeight || 56) - PADDING)

  const [totalUnreadCount, setTotalUnreadCount] = useState(0)
  const [position, setPosition] = useState({
    x: Math.max(0, getMaxX(BUTTON_MIN_WIDTH) - INITIAL_OFFSET_X),
    y: getMaxY(56)
  })
  const [dragging, setDragging] = useState(false)
  const dragRef = useRef(null)
  const offset = useRef({ x: 0, y: 0 })
  const moved = useRef(false)
  const isChatPanelOpenRef = useRef(isChatPanelOpen)
  const onUnreadCountsChangeRef = useRef(onUnreadCountsChange)

  isChatPanelOpenRef.current = isChatPanelOpen
  onUnreadCountsChangeRef.current = onUnreadCountsChange

  useEffect(() => {
    const handleResize = () => {
      const w = dragRef.current?.offsetWidth
      const h = dragRef.current?.offsetHeight

      setPosition((prev) => ({
        x: Math.min(prev.x, getMaxX(w)),
        y: Math.min(prev.y, getMaxY(h))
      }))
    }

    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  useEffect(() => {
    const timer = requestAnimationFrame(() => {
      const w = dragRef.current?.offsetWidth
      const h = dragRef.current?.offsetHeight

      if (w != null || h != null) {
        setPosition((prev) => ({
          x: Math.min(prev.x, getMaxX(w)),
          y: Math.min(prev.y, getMaxY(h))
        }))
      }
    })

    return () => cancelAnimationFrame(timer)
  }, [])

  const fetchTotalUnreadCount = useCallback(async () => {
    if (!userDetails?.id) {
      setTotalUnreadCount(0)

      if (onUnreadCountsChangeRef.current) {
        onUnreadCountsChangeRef.current({ personal: 0, community: 0, total: 0 })
      }

      return
    }

    try {
      const [personalChatCount, communityCount] = await Promise.all([fetchPersonalChatUnreadCount(userDetails), fetchCommunityUnreadCount(userDetails)])
      const total = personalChatCount + communityCount

      setTotalUnreadCount(total)

      if (onUnreadCountsChangeRef.current) {
        onUnreadCountsChangeRef.current({ personal: personalChatCount, community: communityCount, total })
      }
    } catch (err) {
      setTotalUnreadCount(0)

      if (onUnreadCountsChangeRef.current) {
        onUnreadCountsChangeRef.current({ personal: 0, community: 0, total: 0 })
      }
    }
  }, [userDetails?.id])

  useEffect(() => {
    if (!userDetails?.id) {
      setTotalUnreadCount(0)

      return
    }

    fetchTotalUnreadCount()

    const personalCleanup = subscribeToPersonalChatRealtime({
      channelName: 'personal-chat-realtime',
      userId: userDetails.id,
      onMessage: () => {
        fetchTotalUnreadCount()
      }
    })

    const communityCleanup = subscribeToCommunityChatRealtime({
      channelName: 'community-chat-realtime',
      userId: userDetails.id,
      onMessage: () => {
        fetchTotalUnreadCount()
      }
    })

    return () => {
      personalCleanup()
      communityCleanup()
    }
  }, [userDetails?.id, fetchTotalUnreadCount])

  useEffect(() => {
    if (unreadCountRefreshRef) {
      unreadCountRefreshRef.current = fetchTotalUnreadCount

      return () => {
        unreadCountRefreshRef.current = null
      }
    }
  }, [fetchTotalUnreadCount, unreadCountRefreshRef])

  useEffect(() => {
    window.addEventListener('mousemove', handleMouseMove)
    window.addEventListener('mouseup', handleMouseUp)

    return () => {
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('mouseup', handleMouseUp)
    }
  }, [dragging])

  const handleMouseDown = (e) => {
    setDragging(true)
    moved.current = false

    offset.current = {
      x: e.clientX - position.x,
      y: e.clientY - position.y
    }
  }

  const handleMouseMove = (e) => {
    if (!dragging) return

    moved.current = true
    const buttonWidth = dragRef.current?.offsetWidth || BUTTON_MIN_WIDTH
    const buttonHeight = dragRef.current?.offsetHeight || 56
    const newX = e.clientX - offset.current.x
    const newY = e.clientY - offset.current.y

    setPosition({
      x: Math.max(0, Math.min(getMaxX(buttonWidth), newX)),
      y: Math.max(0, Math.min(getMaxY(buttonHeight), newY))
    })
  }

  const handleMouseUp = () => {
    setDragging(false)
  }

  const handleChatOpen = () => {
    if (!moved.current) {
      setIsChatPanelOpen((prev) => !prev)
    }
  }

  return (
    <Button
      ref={dragRef}
      label={t('COFFEE_CHAT')}
      className="floating-chat-button"
      onMouseDown={handleMouseDown}
      onClick={handleChatOpen}
      style={{
        left: `${position.x}px`,
        top: `${position.y}px`,
        right: 'auto',
        bottom: 'auto'
      }}
    >
      <img src="https://coffeeweb.s3.amazonaws.com/mbzghnkm.unw-Dev-CoffeeChats-General.png" alt="chat-logo" className="message-icon" />
      {totalUnreadCount > 0 && <span className="floating-chat-button-badge">{totalUnreadCount > 99 ? '99+' : totalUnreadCount}</span>}
    </Button>
  )
}

export default DraggableChatButton
